import { computed, ref, watch, type Ref } from 'vue';
import {
    BadgeCategory,
    BadgeTier,
    type BadgeAggregateCounts,
    type ResolvedUserBadge,
    type UserBadge,
} from '../models/Badge';
import { BADGE_CATALOG, isProgressionBadge } from '../utils/badgeCatalog';
import { getActivityCountsForUser, getBadgesForUser } from '../services/api/badgesService';
import { logError } from '../utils/logger';

export type UseBadgesForUserResult = {
    badges: Ref<ResolvedUserBadge[] | undefined>;
    /** Current consecutive-event-attendance streak. 0 if the user has never
     *  attended a concluded event or their last event in the series was a miss. */
    currentStreak: Ref<number>;
    /** Longest historical streak. Drives the streak badge tiers and the
     *  "next-tier" hint shown by the indicator's explainer. */
    longestStreak: Ref<number>;
    /**
     * Live counts that feed the badge thresholds. The MyBadges route consumes
     * this directly to render progress bars even for categories where the
     * user hasn't earned any badge yet (no `ResolvedUserBadge` row to read
     * `count` from).
     */
    counts: Ref<BadgeAggregateCounts>;
    isLoading: Ref<boolean>;
    refresh: () => Promise<void>;
};

const ZERO_COUNTS: BadgeAggregateCounts = {
    gmCount: 0,
    playerCount: 0,
    systemsPlayedCount: 0,
    systemsGmedCount: 0,
    maxRepeatedGmedCount: 0,
    currentStreak: 0,
    longestStreak: 0,
    mentorsCount: 0,
    uniquePlayersGmedCount: 0,
};

const TIER_RANK: Record<BadgeTier, number> = {
    [BadgeTier.Wood]: 0,
    [BadgeTier.Bronze]: 1,
    [BadgeTier.Silver]: 2,
    [BadgeTier.Gold]: 3,
};

/**
 * Fetches a user's badges + live activity counts, resolves each row against
 * the catalog, and (for progression categories) keeps only the highest-tier
 * row per category. The SQL recompute stores every threshold ever crossed;
 * this helper collapses that history into the single chip the UI wants to
 * show and stamps the chip with the user's current count for the tooltip.
 */
export function useBadgesForUser(
    userIdGetter: () => string | undefined,
): UseBadgesForUserResult {
    const badges = ref<ResolvedUserBadge[]>();
    const currentStreak = ref(0);
    const longestStreak = ref(0);
    const counts = ref<BadgeAggregateCounts>({ ...ZERO_COUNTS });
    const isLoading = ref(false);
    let currentRequestId = 0;

    async function load(userId: string | undefined): Promise<void> {
        const requestId = ++currentRequestId;

        if (!userId) {
            badges.value = [];
            currentStreak.value = 0;
            longestStreak.value = 0;
            counts.value = { ...ZERO_COUNTS };
            return;
        }

        isLoading.value = true;
        try {
            const [rawBadges, fetchedCounts] = await Promise.all([
                getBadgesForUser(userId),
                getActivityCountsForUser(userId),
            ]);

            if (requestId !== currentRequestId) {
                return;
            }

            badges.value = resolveBadges(rawBadges, fetchedCounts);
            currentStreak.value = fetchedCounts.currentStreak;
            longestStreak.value = fetchedCounts.longestStreak;
            counts.value = fetchedCounts;
        } catch (error) {
            if (requestId !== currentRequestId) {
                return;
            }

            logError(error);
            badges.value = [];
            currentStreak.value = 0;
            longestStreak.value = 0;
            counts.value = { ...ZERO_COUNTS };
        } finally {
            if (requestId === currentRequestId) {
                isLoading.value = false;
            }
        }
    }

    watch(
        computed(() => userIdGetter()),
        (userId) => {
            void load(userId);
        },
        { immediate: true },
    );

    return {
        badges,
        currentStreak,
        longestStreak,
        counts,
        isLoading,
        refresh: () => load(userIdGetter()),
    };
}

export function resolveBadges(
    rows: UserBadge[],
    counts: BadgeAggregateCounts,
): ResolvedUserBadge[] {
    const bestPerCategory = new Map<BadgeCategory, ResolvedUserBadge>();
    const specials: ResolvedUserBadge[] = [];

    for (const row of rows) {
        const definition = BADGE_CATALOG[row.key];

        if (!definition) {
            continue;
        }

        const resolved: ResolvedUserBadge = {
            ...row,
            count: countForCategory(definition.category, counts),
            definition,
        };

        if (!isProgressionBadge(definition)) {
            specials.push(resolved);
            continue;
        }

        const current = bestPerCategory.get(definition.category);
        if (current == null || rankFor(resolved) > rankFor(current)) {
            bestPerCategory.set(definition.category, resolved);
        }
    }

    return [...bestPerCategory.values(), ...specials];
}

function countForCategory(
    category: BadgeCategory,
    counts: BadgeAggregateCounts,
): number | undefined {
    if (category === BadgeCategory.Player) {
        return counts.playerCount;
    }
    if (category === BadgeCategory.GM) {
        return counts.gmCount;
    }
    if (category === BadgeCategory.SystemsPlayed) {
        return counts.systemsPlayedCount;
    }
    if (category === BadgeCategory.SystemsGmed) {
        return counts.systemsGmedCount;
    }
    if (category === BadgeCategory.RepeatedGamesGmed) {
        return counts.maxRepeatedGmedCount;
    }
    if (category === BadgeCategory.Streak) {
        // The badge represents the longest historical streak — current streak
        // drives the standalone flame indicator on Profile, not the chip count.
        return counts.longestStreak;
    }
    if (category === BadgeCategory.Mentors) {
        return counts.mentorsCount;
    }
    if (category === BadgeCategory.UniquePlayersGmed) {
        return counts.uniquePlayersGmedCount;
    }
    return undefined;
}

function rankFor(badge: ResolvedUserBadge): number {
    if (!isProgressionBadge(badge.definition)) {
        return Number.MAX_SAFE_INTEGER;
    }

    return TIER_RANK[badge.definition.tier];
}

// Re-exported so external callers (e.g. specs) can sanity-check the enum source.
export { BadgeCategory, BadgeTier };
